import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { graduationService } from "../services/graduation.service";
import type {
  GraduationPaymentMethod,
  GraduationResponseDTO,
  GraduationSearchParams,
} from "../types/graduation.types";

export interface GraduationListFilters {
  eventName?: string;
  startDate?: string;
  endDate?: string;
  paymentMethod?: GraduationPaymentMethod[];
  tagIds?: number[];
}

interface UseGraduationListOptions {
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 10;

export function useGraduationList({
  pageSize = DEFAULT_PAGE_SIZE,
}: UseGraduationListOptions = {}) {
  const [data, setData] = useState<GraduationResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<GraduationListFilters>({});
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const searchParams: GraduationSearchParams = {
          ...filters,
          page: targetPage,
          size: pageSize,
          sortBy: "eventDate",
          sortDirection: "DESC",
        };
        const response = await graduationService.search(searchParams);
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (error: unknown) {
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(
          error,
          "Erro ao carregar formaturas",
        );
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [filters, pageSize],
  );

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(0);
  }, [load]);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.idGraduation !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const replaceLocal = useCallback((updated: GraduationResponseDTO) => {
    setData((prev) =>
      prev.map((item) =>
        item.idGraduation === updated.idGraduation ? updated : item,
      ),
    );
  }, []);

  const prependLocal = useCallback((created: GraduationResponseDTO) => {
    setData((prev) => [created, ...prev]);
    setTotalElements((prev) => prev + 1);
  }, []);

  const reload = useCallback(() => {
    load(page);
  }, [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    setPage: load,
    reload,
    removeLocal,
    replaceLocal,
    prependLocal,
  };
}
